import { useEffect } from "react";

/**
 * Fires page-time milestones for Lessons 1, 2, 3:
 *   - L1 only: absolute thresholds at 3:50 and 6:20
 *   - L1/2/3: relative thresholds at 50% and 80% of the video duration
 *
 * Each event is fired AT MOST ONCE per user (deduped via localStorage). The
 * caller passes a `fireTrack` fn and a ref to the Vimeo player.
 */
export default function usePageTimeTracking({ user, meta, n, playerRef, fireTrack }) {
  useEffect(() => {
    if (!user || !meta || ![1, 2, 3].includes(n)) return;
    const timers = [];

    const fireOnce = (eventName, extra = {}) => {
      const key = `np_pagetime_fired:${user.id}:${eventName}`;
      try { if (localStorage.getItem(key)) return; } catch { /* ignore */ }
      try { localStorage.setItem(key, new Date().toISOString()); } catch { /* ignore */ }
      fireTrack(eventName, extra);
    };

    // Absolute thresholds — only Lezione 1
    if (n === 1) {
      timers.push(setTimeout(() => fireOnce("tempo_pagina_3m50s_lezione_1", { threshold_s: 230 }), 230 * 1000));
      timers.push(setTimeout(() => fireOnce("tempo_pagina_6m20s_lezione_1", { threshold_s: 380 }), 380 * 1000));
    }

    // Relative thresholds — need video duration first
    let cancelled = false;
    const waitForDuration = async () => {
      for (let tries = 0; tries < 40 && !cancelled; tries++) {
        if (playerRef.current) {
          try {
            const dur = await playerRef.current.getDuration();
            if (dur && dur > 0) return dur;
          } catch { /* ignore */ }
        }
        await new Promise(r => setTimeout(r, 500));
      }
      return null;
    };
    waitForDuration().then((dur) => {
      if (cancelled || !dur) return;
      const at50 = Math.round(dur * 0.5);
      const at80 = Math.round(dur * 0.8);
      timers.push(setTimeout(() => fireOnce(`tempo_pagina_50pct_lezione_${n}`, { video_duration_s: Math.round(dur), threshold_s: at50 }), at50 * 1000));
      timers.push(setTimeout(() => fireOnce(`tempo_pagina_80pct_lezione_${n}`, { video_duration_s: Math.round(dur), threshold_s: at80 }), at80 * 1000));
    });

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [user, n, playerRef]);
}
